import React from 'react'
import { notification } from 'antd'
import StarOn from 'shared/components/Icons/star_on.png'
import StarOff from 'shared/components/Icons/star_off.png'
import { AssetDeliveryGroup } from 'components/shared/AssetDelivery'
import { uploaderIsDisabled } from 'components/shared/AssetDelivery/utils'
import { AssetUpload } from './types'
import { TAB_KEY_CODE, ESC_KEY_CODE, ENTER_KEY_CODE } from './utils'
import UploadGroupLabels from './UploadGroupLabels'
import AssetsCommentsForEditing from './AssetCommentsForEditing'
import { useUploader } from './UploaderContext'

export interface Props {
  isExpanded: boolean
  group: string
  uploaders: AssetUpload[]
  setIsExpanded: (isExpanded: boolean) => void
  expandNextGroup: () => void
  expandPreviousGroup: () => void
}

export default function UploadGroup({
  isExpanded = false,
  group,
  uploaders,
  setIsExpanded,
  expandNextGroup,
  expandPreviousGroup,
}: Props) {
  const { product, dispatch } = useUploader()

  const primaryGroupUpload =
    uploaders.find((u) => u.asset.masterForGroupId) || uploaders[0]

  function handleDragEnter(event: React.DragEvent<HTMLElement>) {
    event.stopPropagation()
  }

  function handleDragLeave(event: React.DragEvent<HTMLElement>) {
    event.stopPropagation()
  }

  function handleDragStart(event: React.DragEvent<HTMLElement>) {
    event.stopPropagation()
    event.dataTransfer.setData('text/plain', group)
  }

  function handleDragOver(event: React.DragEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'copy'
  }

  function handleDrop(event: React.DragEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()

    // Don't allow drops in groups that are disabled
    const toGroup = uploaders.find((u) => u.asset.groupId === group)
    if (toGroup && uploaderIsDisabled(toGroup)) {
      notification.info({
        message: 'Unable to add picture to stack',
        description:
          'Please wait until the images are finished updating before stacking them.',
      })
      return
    }
    const dragGroupId = event.dataTransfer.getData('text/plain')
    if (dragGroupId !== group) {
      dispatch({ type: 'MOVE_TO_GROUP', from: dragGroupId, to: group })
    }
    event.dataTransfer.clearData()
  }

  function handleBlur(event: React.FocusEvent<HTMLElement>) {
    const currentTarget = event.currentTarget
    setTimeout(() => {
      // document.activeElement is only available after current
      // blur event, which is why setTimeout is used to push the
      // following to the end of the event loop.
      if (isExpanded && !currentTarget.contains(document.activeElement)) {
        setIsExpanded(false)
      }
    }, 0)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (!isExpanded) return
    if (event.keyCode === ESC_KEY_CODE) {
      setIsExpanded(false)
    }

    if (event.keyCode === ENTER_KEY_CODE) {
      expandNextGroup()
    }

    if (event.keyCode === TAB_KEY_CODE) {
      if (event.shiftKey) {
        expandPreviousGroup()
      } else {
        expandNextGroup()
      }
    }
  }

  const masterIsDisabled = uploaderIsDisabled(primaryGroupUpload)

  return (
    <AssetDeliveryGroup
      uploaders={uploaders}
      className={isExpanded ? 'opened' : 'closed'}
      preDefinedLabels={product.availablePhotoLabels}
      draggable={!masterIsDisabled && !isExpanded && uploaders.length === 1}
      onDragStart={handleDragStart}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClickPrimaryUpload={() => !isExpanded && setIsExpanded(true)}
      onClickAdditionalUpload={(upload: any) => {
        if (!uploaderIsDisabled(upload)) {
          dispatch({ type: 'REMOVE_FROM_GROUP', upload })
        }
      }}
    >
      {isExpanded && (
        <div className="meta-popup">
          <UploadGroupLabels
            asset={primaryGroupUpload.asset}
            preDefinedLabels={product.availablePhotoLabels}
            onChange={(labels) => {
              if (!masterIsDisabled) {
                dispatch({
                  type: 'UPDATE_UPLOADS',
                  data: uploaders.map((upload) => ({
                    upload,
                    data: { asset: { labels } },
                  })),
                })
              }
            }}
          />

          <AssetsCommentsForEditing
            availableCommentsForEditing={product.availableCommentsForEditing}
            prevComments={primaryGroupUpload.asset.comment}
            doSave={(comment) => {
              if (!masterIsDisabled) {
                dispatch({
                  type: 'UPDATE_UPLOADS',
                  data: uploaders.map((upload) => ({
                    upload,
                    data: { asset: { comment } },
                  })),
                })
              }
            }}
          />
        </div>
      )}

      <div className="actions">
        <button
          className="delete"
          title="Delete"
          onClick={() => {
            if (!masterIsDisabled) {
              dispatch({ type: 'SHOULD_DELETE_GROUP', group })
            }
          }}
        >
          X
        </button>
        <button
          className="star-mark"
          title="Star mark"
          onClick={() => {
            if (!masterIsDisabled) {
              dispatch({
                type: 'UPDATE_UPLOAD',
                upload: primaryGroupUpload,
                data: {
                  asset: { isPrimary: !primaryGroupUpload.asset.isPrimary },
                },
              })
            }
          }}
        >
          <img
            src={primaryGroupUpload.asset.isPrimary ? StarOn : StarOff}
            alt=""
          />
        </button>
      </div>
    </AssetDeliveryGroup>
  )
}
